import { ValuationFormValues } from "@/store/valuationFormStore";
import React, { useState } from "react";
import { useFormContext } from "react-hook-form";

// Define the types
interface Adjustment {
  location?: number;
  size?: number;
  condition?: number;
}

interface Comparable {
  id: number;
  location: string;
  area: number;
  price: number;
  adjustments: Adjustment;
}

interface ZoningFactors {
  [key: string]: number;
}

// Zoning Impact Calculator logic
const zoningFactors: ZoningFactors = {
  "R1B-Rural residential zone": 1.0,
  "RA-High density residential zone": 1.3,
  "RS-Medium density residential": 1.15,
  "C3-City commercial zone": 1.5,
  "Industrial zone": 1.2,
};

const calculateZoningImpact = (zone: string): number => {
  return zoningFactors[zone] || 1.0;
};

// Depreciation Calculator logic
const calculateDepreciation = (
  age: number,
  lifespan: number,
  condition: number
): number => {
  const ageDepreciation = Math.min(age / lifespan, 1);
  const conditionFactor = 1 + (condition - 5) * 0.04;
  return ageDepreciation * conditionFactor;
};

// Main Property Valuation Component
const PropertyValuationModel: React.FC = () => {
  const {
    register,
    formState: { errors },
    watch,
  } = useFormContext<ValuationFormValues>();

  const [comparables, setComparables] = useState<Comparable[]>([
    {
      id: 1,
      location: "Nearby property",
      area: 350,
      price: 88000000,
      adjustments: {
        location: 0,
        size: -2000000,
        condition: 1000000,
      },
    },
  ]);

  const [cmaResult, setCmaResult] = useState<number | null>(null);
  const [depreciationResult, setDepreciationResult] = useState<number | null>(
    null
  );
  const [zoningImpactResult, setZoningImpactResult] = useState<number | null>(
    null
  );
  const [finalValue, setFinalValue] = useState<number | null>(null);

  // Watch form values for property details
  const areaValue = watch("area") || 0;
  const ageValue = watch("age") || 0;
  const conditionValue = watch("condition") || 0;
  const zoningValue = watch("zoning") || "";

  // Comparative Market Analysis (CMA) logic
  const calculateCMA = (propertyArea: number): number => {
    const adjustedValues = comparables.map((comp) => {
      const adjustedValue =
        comp.price +
        (comp.adjustments.location ?? 0) +
        (comp.adjustments.size ?? 0) +
        (comp.adjustments.condition ?? 0);
      return adjustedValue / comp.area;
    });

    if (adjustedValues.length === 0) return 0;

    const averageRate =
      adjustedValues.reduce((sum, rate) => sum + rate, 0) /
      adjustedValues.length;
    return averageRate * propertyArea;
  };

  const handleCalculateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const cmaValue = calculateCMA(areaValue);
    setCmaResult(cmaValue);

    const depreciationFactor = calculateDepreciation(
      ageValue,
      50,
      conditionValue
    );
    setDepreciationResult(depreciationFactor);

    const zoningFactor = calculateZoningImpact(zoningValue);
    setZoningImpactResult(zoningFactor);

    const combinedValue = cmaValue * (1 - depreciationFactor) * zoningFactor;
    setFinalValue(combinedValue);
  };

  const handleComparableChange = (index: number, field: string, value: any) => {
    const newComparables = [...comparables];
    
    if (field === "location" || field === "area" || field === "price") {
      // Handle direct comparable properties
      if (field === "area" || field === "price") {
        (newComparables[index] as any)[field] = parseFloat(value) || 0;
      } else {
        (newComparables[index] as any)[field] = value;
      }
    } else if (field === "locationAdjustment") {
      newComparables[index].adjustments.location = parseFloat(value) || 0;
    } else if (field === "sizeAdjustment") {
      newComparables[index].adjustments.size = parseFloat(value) || 0;
    } else if (field === "conditionAdjustment") {
      newComparables[index].adjustments.condition = parseFloat(value) || 0;
    }
    
    setComparables(newComparables);
  };

  // Add a new comparable
  const addComparable = () => {
    const newComparable: Comparable = {
      id: comparables.length + 1,
      location: "",
      area: 0,
      price: 0,
      adjustments: {
        location: 0,
        size: 0,
        condition: 0,
      },
    };
    setComparables([...comparables, newComparable]);
  };

  // Remove a comparable
  const removeComparable = (id: number) => {
    setComparables(comparables.filter((comp) => comp.id !== id));
  };

  return (
    <div className="bg-gradient-to-b">
      <div className="max-w-6xl mx-auto">
        {/* Property Details Form */}
        <div className="bg-white rounded-xl mb-2 transition-all duration-300 hover:shadow-xl">
          <h2 className="text-lg text-gray-800 my-2 font-bold">
            Comparative Analysis
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-600">
                Area (m²)
              </span>
              <input
                type="number"
                {...register("area", {
                  required: "Total area is required",
                  valueAsNumber: true,
                  min: { value: 1, message: "Area must be greater than 0" },
                })}
                className="mt-1 block w-full rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
              />
              {errors.area && (
                <p className="mt-1 text-sm text-red-600">{errors.area.message}</p>
              )}
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-600">
                Age (years)
              </span>
              <input
                type="number"
                {...register("age", {
                  required: "Property age is required",
                  valueAsNumber: true,
                  min: { value: 0, message: "Age cannot be negative" },
                })}
                className="mt-1 block w-full rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
              />
              {errors.age && (
                <p className="mt-1 text-sm text-red-600">{errors.age.message}</p>
              )}
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-600">
                Condition (1-10)
              </span>
              <input
                type="number"
                {...register("condition", {
                  valueAsNumber: true,
                  min: { value: 1, message: "Condition must be between 1-10" },
                  max: { value: 10, message: "Condition must be between 1-10" },
                })}
                className="mt-1 block w-full rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
              />
              {errors.condition && (
                <p className="mt-1 text-sm text-red-600">{errors.condition.message}</p>
              )}
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-600">Zoning</span>
              <select
                {...register("zoning", { required: "Zoning is required" })}
                className="mt-1 block w-full rounded-lg border border-gray-300 p-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
              >
                <option value="">Select zoning type</option>
                {Object.keys(zoningFactors).map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
              {errors.zoning && (
                <p className="mt-1 text-sm text-red-600">{errors.zoning.message}</p>
              )}
            </label>
          </div>
        </div>

        {/* Comparative Analysis Section */}
        <div className="bg-white rounded-xl mb-2 transition-all duration-300 hover:shadow-xl p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg text-gray-800 font-bold">
              Comparables
            </h2>
            <button
              type="button"
              onClick={addComparable}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-200 focus:ring-offset-2 transition-all duration-200"
            >
              Add Comparable
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Property
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Area
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Adjustments
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {comparables.map((comp, index) => (
                  <tr
                    key={comp.id}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="px-4 w-32 py-4 text-sm font-medium text-gray-900">
                      <div className="flex">
                        <span className="px-1">Comparable</span>
                        <span>{comp.id}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <input
                        type="text"
                        value={comp.location}
                        onChange={(e) => handleComparableChange(index, "location", e.target.value)}
                        className="w-full rounded border border-gray-300 p-2 text-sm"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <input
                        type="number"
                        value={comp.area}
                        onChange={(e) => handleComparableChange(index, "area", e.target.value)}
                        className="w-20 rounded border border-gray-300 p-2 text-sm"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <input
                        type="number"
                        value={comp.price}
                        onChange={(e) => handleComparableChange(index, "price", e.target.value)}
                        className="w-32 rounded border border-gray-300 p-2 text-sm"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Location:</span>
                          <input
                            type="number"
                            value={comp.adjustments.location ?? 0}
                            onChange={(e) => handleComparableChange(index, "locationAdjustment", e.target.value)}
                            className="w-24 rounded border border-gray-300 p-1 text-sm"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Size:</span>
                          <input
                            type="number"
                            value={comp.adjustments.size ?? 0}
                            onChange={(e) => handleComparableChange(index, "sizeAdjustment", e.target.value)}
                            className="w-24 rounded border border-gray-300 p-1 text-sm"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Condition:</span>
                          <input
                            type="number"
                            value={comp.adjustments.condition ?? 0}
                            onChange={(e) => handleComparableChange(index, "conditionAdjustment", e.target.value)}
                            className="w-24 rounded border border-gray-300 p-1 text-sm"
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        type="button"
                        onClick={() => removeComparable(comp.id)}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Calculate Button */}
        <div className="flex justify-end mb-8">
          <button
            type="button"
            onClick={handleCalculateClick}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-200 focus:ring-offset-2 transition-all duration-200"
          >
            Calculate Estimated Value
          </button>
        </div>

        {/* Results Display */}
        {finalValue !== null && (
          <div className="p-6 bg-white rounded-xl shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
              Estimated Property Value
            </h3>
            <p className="text-4xl font-bold text-indigo-600 text-center mb-6">            
              
              <span className="mr-2">RWF</span> 
              <span className="mr-2">{finalValue.toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-indigo-50 rounded-lg text-center">
                <p className="text-sm font-medium text-gray-600">CMA Value</p>
                <p className="text-lg font-semibold text-indigo-600">
               <span className="mr-2">RWF</span> 
                  {cmaResult?.toLocaleString("en-US", {
                    maximumFractionDigits: 0,
                  })}
                </p>
              </div>
              <div className="p-4 bg-indigo-50 rounded-lg text-center">
                <p className="text-sm font-medium text-gray-600">
                  Depreciation Factor
                </p>
                <p className="text-lg font-semibold text-indigo-600">
                  {depreciationResult?.toFixed(2)}
                </p>
              </div>
              <div className="p-4 bg-indigo-50 rounded-lg text-center">
                <p className="text-sm font-medium text-gray-600">
                  Zoning Impact
                </p>
                <p className="text-lg font-semibold text-indigo-600">
                  {zoningImpactResult?.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyValuationModel;